/**
 * The admin console (D92): the install itself, apart from every business.
 *
 * Nothing here touches the business app's session, offline store or sync
 * engine, and nothing in the business app touches this. It has its own token,
 * its own client and its own frame.
 */
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AdminApiError, adminApi, onAdminSignedOut, readAdminToken, writeAdminToken } from "./api";
import { AdminAudit } from "./AdminAudit";
import { AdminBusinesses } from "./AdminBusinesses";
import { AdminLogin } from "./AdminLogin";
import { AdminSetup } from "./AdminSetup";
import { adminView } from "./helpers";
import type { AdminMe, AdminToken } from "./types";

import "./admin.css";

type Tab = "businesses" | "audit";

export function AdminConsole() {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(readAdminToken);
  const [me, setMe] = useState<AdminMe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("businesses");

  const leave = useCallback(() => {
    writeAdminToken(null);
    setToken(null);
    setMe(null);
    setLoadError(null);
    queryClient.removeQueries({ queryKey: ["admin"] });
  }, [queryClient]);

  // The client tells us when the server has stopped accepting the token.
  useEffect(() => onAdminSignedOut(leave), [leave]);

  // A stored token is a claim. Ask the server which stage it is at.
  const load = useCallback(() => {
    setLoadError(null);
    adminApi.me().then(setMe, (error: unknown) => {
      // A 401 has already sent us back to the login screen.
      if (!(error instanceof AdminApiError && error.status === 401)) {
        setLoadError(error instanceof Error ? error.message : "Couldn't reach the server.");
      }
    });
  }, []);

  useEffect(() => {
    if (token && !me) load();
    // Only when the token appears. Taking a token from a sign-in sets `me` with it.
  }, [token]);

  const adopt = useCallback((t: AdminToken) => {
    writeAdminToken(t.access_token);
    setToken(t.access_token);
    setMe({
      email: t.email,
      full_name: t.full_name,
      stage: t.stage,
      must_change_password: t.must_change_password,
      totp_enabled: t.totp_enabled,
    });
  }, []);

  const view = adminView(token, me?.stage ?? null);

  return (
    <div className="adm">
      <header className="adm-bar">
        <span className="adm-brand">Keystone admin</span>
        <span className="adm-who" title={me?.email}>
          {me?.email ?? ""}
        </span>
        {token && (
          <button className="adm-out" type="button" onClick={leave}>
            Sign out
          </button>
        )}
      </header>

      {view === "console" && (
        <nav className="adm-tabs" aria-label="Admin">
          <button
            className="adm-tab"
            type="button"
            aria-current={tab === "businesses" ? "page" : undefined}
            onClick={() => setTab("businesses")}
          >
            Businesses
          </button>
          <button
            className="adm-tab"
            type="button"
            aria-current={tab === "audit" ? "page" : undefined}
            onClick={() => setTab("audit")}
          >
            Audit log
          </button>
        </nav>
      )}

      <main className="adm-main">
        {view === "login" && <AdminLogin onToken={adopt} />}
        {view === "loading" && (
          <div className="adm-center">
            {loadError ? (
              <div className="notice notice-crit" role="alert">
                <p>{loadError}</p>
                <div className="btn-row" style={{ marginTop: 8 }}>
                  <button className="btn" type="button" onClick={load}>
                    Try again
                  </button>
                </div>
              </div>
            ) : (
              <p className="muted" role="status">
                Checking your sign-in…
              </p>
            )}
          </div>
        )}
        {view === "setup" && me && <AdminSetup me={me} onToken={adopt} />}
        {view === "console" && (tab === "businesses" ? <AdminBusinesses /> : <AdminAudit />)}
      </main>
    </div>
  );
}
